/**
 * Building documents store address scalars on the document root (tri-server / Mongo).
 * Do not use nested `building.address`.
 */

export type BuildingFlatAddress = {
    street?: string;
    streetNumber?: string;
    postalCode?: string;
    city?: string;
    country?: string;
};

/**
 * tri-model `Building` instances from `UserAPI.query` may not expose flat fields as own
 * properties; JSON round-trip matches Mongo document shape. Also reads composite `id` when present.
 */
function readFlatFieldsFromUnknown(b: unknown): {
    street: string;
    streetNumber: string;
    postalCode: string;
    city: string;
    country: string;
    compositeId: string;
} {
    const empty = {
        street: '',
        streetNumber: '',
        postalCode: '',
        city: '',
        country: '',
        compositeId: '',
    };
    if (b == null || typeof b !== 'object') {
        return empty;
    }
    let plain: Record<string, unknown> = {};
    try {
        plain = JSON.parse(JSON.stringify(b)) as Record<string, unknown>;
    } catch {
        plain = {};
    }
    const direct = b as Record<string, unknown>;
    const pick = (key: string) => String(plain[key] ?? direct[key] ?? '').trim();
    const rawId = plain.id ?? direct.id;
    const compositeId =
        typeof rawId === 'string' ? rawId.trim() : rawId != null ? String(rawId).trim() : '';
    return {
        street: pick('street'),
        streetNumber: pick('streetNumber'),
        postalCode: pick('postalCode'),
        city: pick('city'),
        country: pick('country'),
        compositeId,
    };
}

export function buildingHasFlatAddress(b: BuildingFlatAddress | null | undefined): boolean {
    const x = readFlatFieldsFromUnknown(b);
    return !!(x.street || x.city || x.postalCode);
}

export function buildingStreetLine(b: BuildingFlatAddress | null | undefined): string {
    const x = readFlatFieldsFromUnknown(b);
    return [x.street, x.streetNumber].filter(Boolean).join(' ');
}

export function buildingPostalCityLine(b: BuildingFlatAddress | null | undefined): string {
    const x = readFlatFieldsFromUnknown(b);
    return [x.postalCode, x.city].filter(Boolean).join(' ');
}

/** e.g. "Hospitalstraße 1, 65549 Limburg an der Lahn, DE" */
export function buildingFullAddressLine(b: BuildingFlatAddress | null | undefined): string {
    const x = readFlatFieldsFromUnknown(b);
    const left = [x.street, x.streetNumber].filter(Boolean).join(' ');
    const mid = [x.postalCode, x.city].filter(Boolean).join(' ');
    const parts = [left, mid].filter(Boolean).join(', ');
    if (parts) {
        return x.country ? `${parts}, ${x.country}` : parts;
    }
    if (x.compositeId.includes(',') && x.compositeId.length > 8) {
        return x.compositeId;
    }
    return '';
}
